import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  fetchLessonSession,
  lessonTaskActions,
  startLessonSession,
  lessonTaskApi,
  tSharedAppDispatch,
  tSharedAppState
} from 'common/modules/ReactShared';

export type tExpressTaskPayload = NonNullable<
  Awaited<ReturnType<typeof lessonTaskApi.fetchLessonTask>>['task']
>;

interface IExpressLessonState {
  lessonId: number | null;
  tasks: tExpressTaskPayload[];
  isLoading: boolean;
  /** Some task contents could not be loaded; the test waits for a retry. */
  loadFailed: boolean;
  /** The task request whose response the state waits for; older ones are ignored. */
  requestId: string | null;
  isStartingTest: boolean;
  /** The last attempt to start the test failed; the test card says so. */
  startTestFailed: boolean;
}

const initialState: IExpressLessonState = {
  lessonId: null,
  tasks: [],
  isLoading: false,
  loadFailed: false,
  requestId: null,
  isStartingTest: false,
  startTestFailed: false
};

/**
 * Loads the content of every task the express lesson page shows at once. A task
 * that fails to load does not discard the others; it is counted so the page can
 * offer a retry.
 */
export const fetchExpressLessonTasks = createAsyncThunk(
  'expressLesson/fetchTasks',
  async ({ lessonId, taskIds }: { lessonId: number; taskIds: number[] }) => {
    const responses = await Promise.allSettled(
      taskIds.map((taskId) => lessonTaskApi.fetchLessonTask(lessonId, taskId))
    );

    return {
      lessonId,
      tasks: responses.flatMap((response) =>
        response.status === 'fulfilled' && response.value.task
          ? [response.value.task]
          : []
      ),
      failedCount: responses.filter(({ status }) => status === 'rejected').length
    };
  }
);

interface IStartExpressTestArgs {
  lessonId: number;
  /** Teach tasks shown on the express lesson page, completed on the student's behalf. */
  teachTaskIds: number[];
  onNavigateToTask(params: { taskId: number }): void;
  onNavigateToResults(): void;
}

/**
 * Leaves the express lesson page for the test. The page has shown the teach
 * tasks, so they are completed first; the server then hands out the next task
 * (the first unfinished test) or, with none left, completes the lesson.
 */
export const startExpressTest = createAsyncThunk<
  void,
  IStartExpressTestArgs,
  { dispatch: tSharedAppDispatch; state: tSharedAppState }
>(
  'expressLesson/startTest',
  async (
    { lessonId, teachTaskIds, onNavigateToTask, onNavigateToResults },
    { dispatch }
  ) => {
    let lessonSessionId: number | null = null;

    // Sequential on purpose: the server creates the lesson session on the first call.
    // eslint-disable-next-line no-restricted-syntax
    for (const taskId of teachTaskIds) {
      // eslint-disable-next-line no-await-in-loop
      const { taskSession } = await lessonTaskApi.fetchTaskSession(lessonId, taskId);
      lessonSessionId = taskSession.lessonSession.id;

      if (taskSession.status !== 'completed') {
        // eslint-disable-next-line no-await-in-loop
        await lessonTaskApi.submitAnswersLessonTask(
          taskSession.url,
          taskSession.taskItemSessions.map((item) => ({ ...item, completed: true }))
        );
      }
    }

    const sessionAction = await dispatch(fetchLessonSession(lessonId));
    let sessionId =
      lessonSessionId ??
      (fetchLessonSession.fulfilled.match(sessionAction)
        ? sessionAction.payload?.id
        : null);

    // With no page tasks nothing above opened a session; the student may have none yet.
    if (!sessionId) {
      const startAction = await dispatch(startLessonSession(lessonId));
      sessionId = startLessonSession.fulfilled.match(startAction)
        ? startAction.payload?.id
        : null;
    }

    if (!sessionId) throw new Error('Express lesson has no lesson session');

    await dispatch(
      lessonTaskActions.nextLessonTask({
        lessonSessionId: sessionId,
        onNavigateToTask,
        onNavigateToResults
      })
    ).unwrap();
  }
);

const expressLessonSlice = createSlice({
  name: 'expressLesson',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchExpressLessonTasks.pending, (state, action) => {
        state.isLoading = true;
        state.loadFailed = false;
        state.requestId = action.meta.requestId;

        if (state.lessonId !== action.meta.arg.lessonId) {
          state.lessonId = action.meta.arg.lessonId;
          state.tasks = [];
          state.startTestFailed = false;
        }
      })
      .addCase(fetchExpressLessonTasks.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.requestId) return;

        state.isLoading = false;
        state.requestId = null;
        state.tasks = action.payload.tasks;
        state.loadFailed = action.payload.failedCount > 0;
      })
      .addCase(fetchExpressLessonTasks.rejected, (state, action) => {
        if (action.meta.requestId !== state.requestId) return;

        state.isLoading = false;
        state.requestId = null;
        state.loadFailed = true;
      })
      .addCase(startExpressTest.pending, (state) => {
        state.isStartingTest = true;
        state.startTestFailed = false;
      })
      .addCase(startExpressTest.fulfilled, (state) => {
        state.isStartingTest = false;
      })
      .addCase(startExpressTest.rejected, (state) => {
        state.isStartingTest = false;
        state.startTestFailed = true;
      });
  }
});

export default expressLessonSlice.reducer;
